import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  ShieldCheck,
  Sparkles,
  Mail,
  Printer,
  Send,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// One page, two modes:
//   • With `?slug=<shop>&t=<claim_token>` in the URL (the link we email
//     out) — show the direct menu-URL submission form. Accepts Dutchie,
//     Jane, Leafly, Weedmaps, or an embedded iframe URL. Zero-friction:
//     no signup, no account, one input.
//   • Without those params — show the general "activate your listing"
//     marketing page for shops that landed here via search or the footer.

const VALUE = [
  {
    icon: Sparkles,
    title: "Free during launch",
    body: "Your basic listing + live menu cost zero during the Colorado & Oregon launch window. After that, 30-day free trial then a modest monthly fee with a discounted annual plan.",
  },
  {
    icon: ShieldCheck,
    title: "You keep your checkout",
    body: "Every Order button on Bud Link deep-links straight to your Dutchie / Jane / Leafly checkout. We're not a marketplace — we're a discovery layer that sends traffic to your existing storefront.",
  },
  {
    icon: Clock,
    title: "Zero maintenance",
    body: "Once we've got your menu URL, your live inventory syncs to Bud Link every hour automatically. You keep managing your menu in your POS like you already do.",
  },
];

const SUPPORTED_HOSTS = [
  "dutchie.com/dispensary/…",
  "iheartjane.com/stores/…",
  "leafly.com/dispensary-info/…",
  "weedmaps.com/dispensaries/…",
];

export default function OnboardingGuide() {
  const [params] = useSearchParams();
  const slug = params.get("slug") || "";
  const token = params.get("t") || "";
  const hasClaim = Boolean(slug && token);

  return (
    <main className="container-narrow py-10 md:py-14 max-w-3xl print:max-w-none print:py-0">
      <div className="flex items-start justify-between gap-4 mb-8 print:hidden">
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
          ← Back home
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="onboarding-print"
          className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
        >
          <Printer className="w-3 h-3" /> Print
        </button>
      </div>

      <div className="eyebrow mb-3">For dispensary owners</div>
      <h1 className="font-serif text-4xl md:text-5xl mb-4 leading-[1.05]">
        {hasClaim
          ? "Activate your Bud Link listing."
          : "Get your shop found on Bud Link."}
      </h1>
      <p className="text-foreground/75 text-lg leading-relaxed max-w-2xl mb-8">
        {hasClaim
          ? "Paste your live-menu URL below and we'll wire your inventory into Bud Link within the hour. Works with Dutchie, Jane, Leafly, Weedmaps, or any embedded-menu URL from your website."
          : "Bud Link is the strain search patients and adult-use customers use to find product at a licensed shop near them. If you got a link from us, use it below. Otherwise, drop us a note and we'll onboard your shop manually."}
      </p>

      {hasClaim ? <ClaimForm slug={slug} token={token} /> : <GeneralCTA />}

      <section className="grid sm:grid-cols-3 gap-3 my-14">
        {VALUE.map((v) => {
          const Icon = v.icon;
          return (
            <div
              key={v.title}
              className="border border-border bg-card rounded-2xl p-5 print:break-inside-avoid"
            >
              <Icon className="w-5 h-5 text-primary mb-3" strokeWidth={1.6} />
              <div className="font-serif text-lg mb-1.5">{v.title}</div>
              <p className="text-sm text-foreground/70 leading-relaxed">
                {v.body}
              </p>
            </div>
          );
        })}
      </section>

      <div className="border-t border-border pt-6 text-xs text-foreground/60 leading-relaxed">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-3 h-3" />
          <a
            href="mailto:BudLink@LinkLitePro.com"
            className="hover:text-foreground"
          >
            BudLink@LinkLitePro.com
          </a>
        </div>
        Questions or want a walkthrough? Email us — we usually reply the same day.
      </div>
    </main>
  );
}

// -------------------------------------------------------------
// Claim form — the primary flow for shops arriving from our emailed link.
// -------------------------------------------------------------
const ClaimForm = ({ slug, token }) => {
  const [menuUrl, setMenuUrl] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const shopName = useMemo(
    () =>
      slug
        .split("-")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" "),
    [slug]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!menuUrl.trim()) {
      toast.error("Paste your live menu URL");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/dispensary/submit-menu", {
        slug,
        claim_token: token,
        menu_url: menuUrl.trim(),
        contact_email: contactEmail.trim() || null,
        contact_name: contactName.trim() || null,
      });
      setResult(r.data);
      toast.success("Menu received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div
        data-testid="onboarding-success"
        className="border border-emerald-500/40 bg-emerald-500/10 rounded-2xl p-6 mb-10 flex items-start gap-4"
      >
        <CheckCircle2 className="w-7 h-7 text-emerald-300 mt-0.5 shrink-0" />
        <div>
          <div className="font-serif text-xl mb-1">
            {result.name} — {result.provider === "unknown" ? "received" : "wired up"}
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed">
            {result.next}
          </p>
          {result.provider !== "unknown" && (
            <p className="text-xs text-foreground/60 mt-3">
              Detected menu provider: <span className="font-mono">{result.provider}</span> ·
              You&apos;ll see products flowing on your listing within the hour.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-primary/30 bg-card rounded-2xl p-6 mb-10">
      <div className="eyebrow mb-2">Shop</div>
      <div className="font-serif text-2xl mb-6" data-testid="onboarding-shop-name">
        {shopName}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <div className="text-xs uppercase tracking-[0.18em] text-foreground/60 mb-2">
            Your live menu URL
          </div>
          <Input
            data-testid="onboarding-menu-url"
            value={menuUrl}
            onChange={(e) => setMenuUrl(e.target.value)}
            placeholder="https://dutchie.com/dispensary/your-shop"
            className="h-11"
            autoFocus
          />
          <div className="text-[11px] text-foreground/50 mt-2 leading-relaxed">
            Works with: {SUPPORTED_HOSTS.join(" · ")}. If your menu is on your own
            website, paste the URL of the page that embeds it — we&apos;ll figure
            out the rest.
          </div>
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <Input
            data-testid="onboarding-contact-name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Your name (optional)"
            className="h-11"
          />
          <Input
            data-testid="onboarding-contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="Your email (optional)"
            className="h-11"
          />
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="onboarding-submit"
          className="w-full h-12 rounded-full text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
        >
          {busy ? (
            "Sending…"
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Activate my listing
            </>
          )}
        </Button>
        <p className="text-[11px] text-foreground/50 leading-relaxed">
          By submitting you authorize Bud Link to display your public menu
          data (product names, prices, images, stock status) on
          budlink.linklitepro.com. No login required — the link you clicked is
          bound to your shop. Revocable anytime by emailing{" "}
          <a href="mailto:BudLink@LinkLitePro.com" className="underline">
            BudLink@LinkLitePro.com
          </a>
          .
        </p>
      </form>
    </div>
  );
};

const GeneralCTA = () => (
  <div className="border border-border bg-card rounded-2xl p-6 mb-10 flex flex-col sm:flex-row items-start gap-4">
    <Mail className="w-8 h-8 text-primary shrink-0" strokeWidth={1.4} />
    <div className="flex-1">
      <div className="font-serif text-xl mb-2">Get in touch</div>
      <p className="text-sm text-foreground/70 leading-relaxed mb-4">
        Send us your shop name, address, and live-menu URL and we&apos;ll set
        up your listing manually. Turnaround is typically same-day.
      </p>
      <a
        href="mailto:BudLink@LinkLitePro.com?subject=New%20Bud%20Link%20listing%20request"
        data-testid="onboarding-generic-email"
        className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium"
      >
        Email BudLink@LinkLitePro.com <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </div>
);
